<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EnergyCertificateType = App.Enums.EstateObject.EnergyCertificateType

const props = defineProps<{
  value?: EnergyCertificateType | null
}>()

const { t } = useI18n()

const scaleClasses = [
  { value: 'a_plus', label: 'A+', color: '#0f9f6e' },
  { value: 'a', label: 'A', color: '#22c55e' },
  { value: 'b', label: 'B', color: '#84cc16' },
  { value: 'c', label: 'C', color: '#c8d92b' },
  { value: 'd', label: 'D', color: '#facc15' },
  { value: 'e', label: 'E', color: '#fb923c' },
  { value: 'f', label: 'F', color: '#f97316' },
  { value: 'g', label: 'G', color: '#ef4444' },
  { value: 'h', label: 'H', color: '#b91c1c' },
] as const

const selectedScaleIndex = computed(() => scaleClasses.findIndex((energyClass) => energyClass.value === props.value))
const hasScaleValue = computed(() => selectedScaleIndex.value >= 0)
const selectedScaleClass = computed(() => (hasScaleValue.value ? scaleClasses[selectedScaleIndex.value] : null))
const label = computed(() => {
  if (!props.value) {
    return t('not_available')
  }
  return t(`enums.energyCertificateType.${props.value}`)
})
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between gap-4">
      <div>
        <p class="text-surface-500 text-sm">{{ t('label') }}</p>
      </div>

      <Tag
        v-if="hasScaleValue"
        :value="label"
        class="text-surface-0! border-none! text-base!"
        :style="{ backgroundColor: selectedScaleClass?.color }" />
    </div>

    <div class="flex gap-1" :aria-label="t('aria_label', { value: label })" role="img">
      <div
        v-for="(energyClass, index) in scaleClasses"
        :key="energyClass.value"
        class="relative flex min-w-0 flex-1 flex-col items-center gap-2">
        <div
          class="h-4 w-full rounded-sm"
          :class="{ 'ring-surface-950 ring-2 ring-offset-2': index === selectedScaleIndex }"
          :style="{ backgroundColor: energyClass.color }" />
        <span
          class="text-xs font-semibold"
          :class="index === selectedScaleIndex ? 'text-surface-950' : 'text-surface-500'">
          {{ energyClass.label }}
        </span>
      </div>
    </div>

    <p v-if="!hasScaleValue" class="text-surface-500 text-sm">
      {{ t('no_scale_value') }}
    </p>
  </div>
</template>

<i18n>
{
  "de": {
    "label": "Energieeffizienzklasse",
    "not_available": "Keine Angabe",
    "no_scale_value": "Für diesen Energieausweis liegt keine Effizienzklasse auf der Farbskala vor.",
    "aria_label": "Energieeffizienzklasse {value}"
  },
  "en": {
    "label": "Energy efficiency class",
    "not_available": "Not specified",
    "no_scale_value": "No efficiency class is available for this energy certificate on the color scale.",
    "aria_label": "Energy efficiency class {value}"
  }
}
</i18n>
